import React from 'react';
import { Card } from 'primereact/card';
import { Skeleton } from 'primereact/skeleton';
import { useUser } from '../../services/contexts/user-context';
import { useGetViewStats } from '../../services/api/analytics-api';
import { useGetFullPortfolio } from '../../services/api/users-api';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { User, Briefcase, Eye, BarChart3, Edit, ExternalLink } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  margin-bottom: 2rem;
  h1 {
    font-size: 1.8rem;
    color: var(--text-main);
    margin: 0 0 0.3rem;
  }
  p {
    color: var(--text-secondary);
    margin: 0;
    font-size: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(Card)`
  .p-card {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    cursor: pointer;
    transition: box-shadow 0.2s;
    &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
  }
  .p-card-body { padding: 1.5rem; }
  .p-card-content { padding: 0; }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.bg || 'var(--primary-color, #6366f1)'};
  color: white;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-main);
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ActionCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: box-shadow 0.2s;
  display: flex;
  align-items: center;
  gap: 1rem;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
`;

const ActionText = styled.div`
  h3 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
    color: var(--text-main);
  }
  p {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-secondary);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: var(--text-main);
  margin: 0 0 1rem;
`;

const CompletionBar = styled.div`
  background: var(--border-color, #e5e5e5);
  border-radius: 8px;
  height: 8px;
  width: 100%;
  margin-top: 0.75rem;
`;

const CompletionFill = styled.div`
  background: var(--primary-color, #6366f1);
  border-radius: 8px;
  height: 100%;
  width: ${props => props.percent}%;
  transition: width 0.3s;
`;

function computeCompletion(portfolio) {
  if (!portfolio) return 0;
  let score = 0;
  const total = 6;
  if (portfolio.profile?.bio) score++;
  if (portfolio.profile?.picture) score++;
  if (portfolio.educations?.length > 0) score++;
  if (portfolio.experiences?.length > 0) score++;
  if (portfolio.projects?.length > 0) score++;
  if (portfolio.skills?.length > 0) score++;
  return Math.round((score / total) * 100);
}

const Dashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const profileId = user?.profile?.id || user?.profile?.profile_id;

  const { data: viewStats, isLoading: statsLoading } = useGetViewStats(profileId);
  const { data: portfolio, isLoading: portfolioLoading } = useGetFullPortfolio();

  const completionPercent = computeCompletion(portfolio);
  const stats = viewStats?.data || viewStats || {};

  return (
    <Container>
      <WelcomeSection>
        <h1>Welcome back, {user?.profile?.profile_name || user?.email || 'User'}</h1>
        <p>Here's an overview of your Portify profile</p>
      </WelcomeSection>

      {/* Profile Completion */}
      <StatCard style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <StatLabel>Profile Completion</StatLabel>
            <StatValue style={{ fontSize: '1.5rem' }}>{portfolioLoading ? '...' : `${completionPercent}%`}</StatValue>
          </div>
          {completionPercent < 100 && (
            <ActionCard style={{ border: 'none', padding: '0.75rem 1rem', cursor: 'pointer' }} onClick={() => navigate('/default/edit-profile')}>
              <Edit size={16} />
              <span style={{ fontSize: '0.85rem', color: 'var(--text-main)' }}>Complete Profile</span>
            </ActionCard>
          )}
        </div>
        <CompletionBar>
          <CompletionFill percent={portfolioLoading ? 0 : completionPercent} />
        </CompletionBar>
      </StatCard>

      {/* Stats */}
      <SectionTitle>Analytics Overview</SectionTitle>
      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon bg="#6366f1"><Eye size={20} /></StatIcon>
          </StatHeader>
          {statsLoading ? <Skeleton height="2rem" /> : <StatValue>{stats.total_views || 0}</StatValue>}
          <StatLabel>Total Profile Views</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bg="#3b82f6"><BarChart3 size={20} /></StatIcon>
          </StatHeader>
          {statsLoading ? <Skeleton height="2rem" /> : <StatValue>{stats.recent_views || 0}</StatValue>}
          <StatLabel>Recent Views (30d)</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bg="#10b981"><BarChart3 size={20} /></StatIcon>
          </StatHeader>
          {statsLoading ? <Skeleton height="2rem" /> : <StatValue>{stats.total_search_appearances || 0}</StatValue>}
          <StatLabel>Search Appearances</StatLabel>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon bg="#f59e0b"><User size={20} /></StatIcon>
          </StatHeader>
          {statsLoading ? <Skeleton height="2rem" /> : <StatValue>{stats.total_unlocks || 0}</StatValue>}
          <StatLabel>Profile Unlocks</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Quick Actions */}
      <SectionTitle>Quick Actions</SectionTitle>
      <QuickActions>
        <ActionCard onClick={() => navigate('/default/edit-profile')}>
          <StatIcon bg="#6366f1"><Edit size={20} /></StatIcon>
          <ActionText>
            <h3>Edit Profile</h3>
            <p>Update your bio, education, and experience</p>
          </ActionText>
        </ActionCard>

        <ActionCard onClick={() => navigate('/default/projects')}>
          <StatIcon bg="#8b5cf6"><Briefcase size={20} /></StatIcon>
          <ActionText>
            <h3>Manage Projects</h3>
            <p>Add or update your portfolio projects</p>
          </ActionText>
        </ActionCard>

        <ActionCard onClick={() => navigate('/default/templates')}>
          <StatIcon bg="#10b981"><ExternalLink size={20} /></StatIcon>
          <ActionText>
            <h3>Change Template</h3>
            <p>Customize your portfolio appearance</p>
          </ActionText>
        </ActionCard>

        <ActionCard onClick={() => window.open(`/profile/${user?.profile?.profile_name}`, '_blank')}>
          <StatIcon bg="#f59e0b"><Eye size={20} /></StatIcon>
          <ActionText>
            <h3>View Portfolio</h3>
            <p>Preview your public portfolio page</p>
          </ActionText>
        </ActionCard>
      </QuickActions>
    </Container>
  );
};

export default Dashboard;
